import { restApi } from '../../api/restApiSlice';

import type Domain from '../Domain';
import { DomainListQueryParameters } from '../DomainListPage/DomainListQueryParameters';

export enum ExportStatus {
  Queued = 'queued',
  NotQueue = 'not queued',
  AlreadyExists = 'already exists',
}

type ExportQueryParameters = Partial<{
  sort: DomainListQueryParameters['sort'];
  sortDirection: DomainListQueryParameters['sortDirection'];
  q: string;
}>;

type DomainsList = {
  domains: { domain: string }[];
  totalCount: number;
};

const domainStatisticsApiSlice = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getDomainStatistics: builder.query<{ data: Domain[]; totalCount: number }, DomainListQueryParameters>({
      query: (params) => ({ url: `/v3/domain-statistics`, method: 'GET', params }),
      providesTags: ['domainStatistics'],
    }),
    exportDomainsCsv: builder.mutation<{ status: ExportStatus }, ExportQueryParameters>({
      query: (params) => ({ url: `/v3/domain-statistics/export`, method: 'POST', params }),
    }),
    deleteDomain: builder.mutation({
      query: (id) => ({ url: `/v3/domain-statistics/${id}`, method: 'DELETE' }),
      invalidatesTags: ['domainStatistics', 'Domains', 'GscConnectionStatus'],
    }),
    getDomains: builder.query<DomainsList, string>({
      query: (query = '') => ({ url: `/v3/domains`, method: 'GET', params: { query } }),
      providesTags: ['Domains'],
    }),
  }),
});

export const {
  useGetDomainStatisticsQuery,
  useLazyGetDomainStatisticsQuery,
  useExportDomainsCsvMutation,
  useDeleteDomainMutation,
  useGetDomainsQuery,
} = domainStatisticsApiSlice;

const domainDetailsApiSlice = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getDomainDetails: builder.query({
      query: (domain) => ({ url: `/v3/domains/${domain}/config`, method: 'GET' }),
      providesTags: ['domainDetails'],
    }),
    getExtraStats: builder.query({
      query: (params) => ({ url: `/v3/analytics/stats`, method: 'GET', params }),
      providesTags: ['domainDetails'],
    }),
    updateDomainDetails: builder.mutation({
      query: ({ domain, ...body }) => ({ url: `/v3/domains/${domain}/config`, method: 'PATCH', body }),
      invalidatesTags: ['domainDetails'],
    }),
  }),
});

export const { useGetDomainDetailsQuery, useUpdateDomainDetailsMutation, useGetExtraStatsQuery } =
  domainDetailsApiSlice;

const accountDetailsApiSlice = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccountDetails: builder.query({
      query: () => ({ url: `/v3/domains/company/config`, method: 'GET' }),
      providesTags: ['AccountDetails'],
    }),
  }),
});

export const { useGetAccountDetailsQuery } = accountDetailsApiSlice;
